// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The board view: real rendered geometry, with A and B composited as an
 * onion-skin and the diff drawn on top.
 *
 * Compositing is the point. Both boards are drawn through the same camera into
 * the same pixels, so a moved track is a doubled track and a re-route is two
 * divergent paths — you see *what* changed, not just where.
 *
 * The diff overlay is drawn by our code (spec §6.7), on the same camera the
 * geometry used, so it lands exactly on the objects it annotates.
 */

import { intersects, union, type BBox, type MaybeBBox } from '../model/bbox.ts';
import type { Board, BoardObject } from '../model/board.ts';
import type { DiffResult, ObjectChange } from '../diff/diff.ts';
import { classifyBox, makeRect, rectBox, type Rect, type Selection } from '../select/index.ts';
import { Camera } from '../render/camera.ts';
import { buildDisplayList, type DisplayList } from '../render/displaylist.ts';
import { BoardRenderer, defaultVisibleLayers, type RenderOptions } from '../render/renderer.ts';
import { layerStyle, sortLayersForUI } from '../render/palette.ts';

export type ViewMode = 'a' | 'b' | 'onion' | 'split';

const CHANGE_COLOR = {
  added: '#3fb950',
  removed: '#f85149',
  modified: '#e3b341',
  moved: '#e3b341',
} as const;

const REGION_COLOR = '#4a9eff';
const SELECTED_COLOR = '#ffffff';
/** Below this, a drag is a click. */
const MIN_DRAW_PX = 4;
/** Board A is tinted cool, Board B warm, so the two are separable at a glance. */
/**
 * Selections are colour-coded and the change list tags its rows to match, so
 * "what is in this box" is answerable at a glance rather than by reading names.
 */
export const SELECTION_COLORS: readonly string[] = [
  '#4a9eff',
  '#f0883e',
  '#3fb950',
  '#d357d2',
  '#e3b341',
  '#2dd4bf',
];

export function selectionColor(index: number): string {
  return SELECTION_COLORS[((index % SELECTION_COLORS.length) + SELECTION_COLORS.length) % SELECTION_COLORS.length]!;
}

export const TINT_A = '#5b8dd6';
export const TINT_B = '#e0703a';

export interface ViewInput {
  a?: Board;
  b?: Board;
  diff?: DiffResult;
  /** The rectangles the user has drawn, in the order they drew them. */
  selections: Selection[];
  /** Which selection is being edited, if any. */
  activeSelectionId?: string;
  /** Change keys the current plan takes. Anything else is ringed as left behind. */
  takenKeys?: ReadonlySet<string>;
  /** True while the user is choosing what to merge. Enables drawing rectangles. */
  merging: boolean;
  /** The rubber band in progress, in board millimetres. */
  drawing?: Rect;
  mode: ViewMode;
  selectedKey?: string;
  /** Layer names to draw. */
  visibleLayers: Set<string>;
  /** 0 = only A, 1 = only B, 0.5 = even blend. Onion mode only. */
  blend: number;
  /**
   * Where the divider sits, as a fraction of the canvas width. Split mode only.
   * Board A is drawn to its left, Board B to its right, through one camera.
   */
  split: number;
  /** Tint each board so they are distinguishable where they overlap. */
  tintBoards: boolean;
  /** Push unchanged geometry into the background. */
  emphasiseChanges: boolean;
  /** Draw the diff annotation boxes and arrows on top. */
  showOverlay: boolean;
}

export interface ViewStats {
  shapes: number;
  culled: number;
  texts: number;
  ms: number;
  placeholdersA: number;
  placeholdersB: number;
}

export class BoardView {
  readonly camera = new Camera();
  private readonly ctx: CanvasRenderingContext2D;
  private readonly renderer: BoardRenderer;
  private input: ViewInput = {
    selections: [],
    merging: false,
    mode: 'onion',
    visibleLayers: new Set(),
    blend: 0.5,
    split: 0.5,
    tintBoards: true,
    emphasiseChanges: false,
    showOverlay: true,
  };

  private listA?: DisplayList;
  private listB?: DisplayList;
  private boardA?: Board;
  private boardB?: Board;
  private fitted = false;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private downX = 0;
  private downY = 0;
  private frame = 0;
  /** True while the split divider is being dragged rather than the board panned. */
  private draggingSplit = false;
  /** Where a rubber-band drag began, in board millimetres. */
  private drawStart?: { x: number; y: number };
  /** Fingers on the canvas, by pointer id, in client coordinates. */
  private touches = new Map<number, { x: number; y: number }>();
  /** Finger spread and midpoint (canvas pixels) at the last pinch step. */
  private pinch?: { dist: number; x: number; y: number };
  /**
   * A pinch ended with a finger still down. That finger does nothing until it
   * lifts; otherwise it would pan, or pick whatever is under it on release.
   */
  private gestureSpent = false;

  onSelect?: (key: string | undefined) => void;
  onStats?: (stats: ViewStats) => void;
  /** Fires as the divider is dragged, so the app can remember where it was. */
  onSplitChange?: (fraction: number) => void;
  /** A rectangle the user dragged out while merging. */
  onRectDrawn?: (rect: Rect) => void;

  readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('This browser does not provide a 2D canvas context.');
    this.ctx = ctx;
    this.renderer = new BoardRenderer(ctx, globalThis.devicePixelRatio || 1);

    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(() => {
        // The first resize is usually the canvas getting its real size, which is
        // the earliest moment a fit can succeed.
        if (!this.fitted) this.fit();
        else this.requestDraw();
      }).observe(canvas);
    }
  }

  /** Display lists are rebuilt only when the boards themselves change. */
  setInput(input: ViewInput, refit = false): void {
    let boardsChanged = false;
    if (input.a !== this.boardA) {
      this.boardA = input.a;
      this.listA = input.a ? buildDisplayList(input.a) : undefined;
      boardsChanged = true;
    }
    if (input.b !== this.boardB) {
      this.boardB = input.b;
      this.listB = input.b ? buildDisplayList(input.b) : undefined;
      boardsChanged = true;
    }
    this.input = input;
    this.syncCursor();

    // A different board can sit at entirely different coordinates — switching
    // repositories is the obvious case — and the old camera would leave it off
    // screen with nothing to show that anything had loaded. Refit only when the
    // new content is not where the camera is already looking, so flipping
    // between revisions of one board keeps the zoom the user set.
    if (boardsChanged && this.fitted) {
      const extent = this.contentExtent();
      const visible = this.camera.visibleBox(this.viewport());
      if (extent === null || !intersects(extent, visible)) this.fitted = false;
    }

    if (refit || !this.fitted) this.fit();
    else this.requestDraw();
  }

  /**
   * Every layer either board draws on, ordered the way KiCad's Appearance panel
   * lists them — front of the board first. Paint order is a separate concern and
   * stays inside the renderer.
   */
  availableLayers(): string[] {
    const names = new Set<string>();
    for (const list of [this.listA, this.listB]) {
      for (const l of list?.layers ?? []) names.add(l);
    }
    return sortLayersForUI([...names]);
  }

  suggestedLayers(): Set<string> {
    const out = new Set<string>();
    for (const list of [this.listA, this.listB]) {
      if (list) for (const l of defaultVisibleLayers(list)) out.add(l);
    }
    return out;
  }

  private viewport(): { width: number; height: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { width: Math.max(1, Math.floor(rect.width)), height: Math.max(1, Math.floor(rect.height)) };
  }

  fit(box?: MaybeBBox): void {
    const target = box ?? this.contentExtent();
    // Only count it if it worked: on the very first render the canvas has not
    // been laid out yet and has no size, and treating that as "fitted" left the
    // board sitting tiny in the corner at default zoom.
    if (this.camera.fit(target, this.viewport())) this.fitted = true;
    this.requestDraw();
  }

  zoomTo(box: MaybeBBox): void {
    if (box === null) return;
    const padding = Math.max((box.maxX - box.minX) * 1.2, (box.maxY - box.minY) * 1.2, 4);
    this.fit({
      minX: box.minX - padding,
      minY: box.minY - padding,
      maxX: box.maxX + padding,
      maxY: box.maxY + padding,
    });
  }

  /** Zoom about the centre of the canvas, for buttons that have no cursor position. */
  zoomBy(factor: number): void {
    const { width, height } = this.viewport();
    this.camera.zoomAt(width / 2, height / 2, factor);
    this.requestDraw();
  }

  /**
   * Extent of what is actually *visible*, not of everything in the file.
   *
   * A board can carry documentation graphics far outside its outline — one of
   * the test boards has drawing-layer content stretching twice the board's
   * width — and fitting to those leaves the board itself as a small island in a
   * sea of empty canvas. Only visible layers count; if that leaves nothing,
   * fall back to the whole file so the view is never blank.
   */
  private contentExtent(): MaybeBBox {
    let out: MaybeBBox = null;
    for (const list of [this.listA, this.listB]) {
      if (!list) continue;
      for (const item of list.shapes) {
        if (!this.input.visibleLayers.has(item.layer)) continue;
        out = union(out, item.bbox);
      }
    }
    if (out === null) {
      for (const board of [this.boardA, this.boardB]) {
        if (board) out = union(out, board.extent());
      }
    }
    for (const s of this.input.selections) out = union(out, rectBox(s.rect));
    return out;
  }

  requestDraw(): void {
    if (this.frame !== 0) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.draw();
    });
  }

  // --- interaction ---------------------------------------------------------------

  private onWheel = (ev: WheelEvent): void => {
    ev.preventDefault();
    const rect = this.canvas.getBoundingClientRect();
    this.camera.zoomAt(ev.clientX - rect.left, ev.clientY - rect.top, Math.exp(-ev.deltaY * 0.0015));
    this.requestDraw();
  };

  private onPointerDown = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') {
      this.touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (this.touches.size === 2) {
        this.startPinch(ev);
        return;
      }
      if (this.touches.size > 2 || this.gestureSpent) return;
    }
    if (this.input.mode === 'split' && this.nearSplit(ev)) {
      this.draggingSplit = true;
      this.canvas.setPointerCapture?.(ev.pointerId);
      return;
    }
    // While merging, a plain drag draws a rectangle. Panning moves to the middle
    // button and to shift-drag, because drawing is what the user came here to do
    // and it should be the gesture that needs no explanation.
    if (this.input.merging && ev.button === 0 && !ev.shiftKey) {
      this.drawStart = this.toWorld(ev);
      this.canvas.setPointerCapture?.(ev.pointerId);
      return;
    }
    this.dragging = true;
    this.lastX = this.downX = ev.clientX;
    this.lastY = this.downY = ev.clientY;
    this.canvas.classList.add('dragging');
    this.canvas.setPointerCapture?.(ev.pointerId);
  };

  private onPointerMove = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch' && this.touches.has(ev.pointerId)) {
      this.touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (this.pinch) {
        this.movePinch();
        return;
      }
      if (this.gestureSpent) return;
    }
    if (this.draggingSplit) {
      this.moveSplit(ev);
      return;
    }
    if (this.drawStart) {
      const to = this.toWorld(ev);
      this.input = { ...this.input, drawing: makeRect(this.drawStart.x, this.drawStart.y, to.x, to.y) };
      this.requestDraw();
      return;
    }
    if (!this.dragging) {
      // Advertise the divider before it is grabbed; otherwise the mode decides.
      if (this.input.mode === 'split') {
        this.canvas.style.cursor = this.nearSplit(ev) ? 'col-resize' : this.modeCursor();
      } else {
        this.syncCursor();
      }
      return;
    }
    this.camera.panByScreen(ev.clientX - this.lastX, ev.clientY - this.lastY);
    this.lastX = ev.clientX;
    this.lastY = ev.clientY;
    this.requestDraw();
  };

  private onPointerUp = (ev: PointerEvent): void => {
    if (ev.pointerType === 'touch') {
      this.touches.delete(ev.pointerId);
      if (this.pinch || this.gestureSpent) {
        this.pinch = undefined;
        this.gestureSpent = this.touches.size > 0;
        if (this.canvas.hasPointerCapture?.(ev.pointerId)) this.canvas.releasePointerCapture(ev.pointerId);
        return;
      }
    }
    if (this.draggingSplit) {
      this.draggingSplit = false;
      if (this.canvas.hasPointerCapture?.(ev.pointerId)) this.canvas.releasePointerCapture(ev.pointerId);
      return;
    }
    if (this.drawStart) {
      const from = this.drawStart;
      this.drawStart = undefined;
      if (this.canvas.hasPointerCapture?.(ev.pointerId)) this.canvas.releasePointerCapture(ev.pointerId);
      const to = this.toWorld(ev);
      const rect = makeRect(from.x, from.y, to.x, to.y);
      this.input = { ...this.input, drawing: undefined };
      this.requestDraw();
      // A click, not a drag: treat it as picking rather than as a rectangle of
      // zero area, which would claim nothing and just clutter the list.
      const dragged = Math.abs(this.camera.toScreenX(rect.maxX) - this.camera.toScreenX(rect.minX)) > MIN_DRAW_PX;
      const tall = Math.abs(this.camera.toScreenY(rect.maxY) - this.camera.toScreenY(rect.minY)) > MIN_DRAW_PX;
      if (dragged && tall) this.onRectDrawn?.(rect);
      else if (this.onSelect) this.onSelect(this.pick(ev));
      return;
    }
    if (!this.dragging) return;
    this.dragging = false;
    this.canvas.classList.remove('dragging');
    if (this.canvas.hasPointerCapture?.(ev.pointerId)) this.canvas.releasePointerCapture(ev.pointerId);
    const moved = Math.abs(ev.clientX - this.downX) > 3 || Math.abs(ev.clientY - this.downY) > 3;
    if (!moved && this.onSelect) this.onSelect(this.pick(ev));
  };

  /**
   * A second finger turns whatever the first one started into a pinch. The
   * half-done gesture is dropped, not completed: no pick, no rectangle.
   */
  private startPinch(ev: PointerEvent): void {
    this.dragging = false;
    this.draggingSplit = false;
    this.drawStart = undefined;
    this.canvas.classList.remove('dragging');
    if (this.input.drawing) {
      this.input = { ...this.input, drawing: undefined };
      this.requestDraw();
    }
    this.canvas.setPointerCapture?.(ev.pointerId);
    this.pinch = this.pinchState();
  }

  /** Zoom by the change in finger spread, and pan with the midpoint, as map apps do. */
  private movePinch(): void {
    const from = this.pinch;
    const to = this.pinchState();
    if (!from || !to) return;
    if (from.dist > 0 && to.dist > 0) this.camera.zoomAt(from.x, from.y, to.dist / from.dist);
    this.camera.panByScreen(to.x - from.x, to.y - from.y);
    this.pinch = to;
    this.requestDraw();
  }

  private pinchState(): { dist: number; x: number; y: number } | undefined {
    const [p, q] = [...this.touches.values()];
    if (!p || !q) return undefined;
    const rect = this.canvas.getBoundingClientRect();
    return {
      dist: Math.hypot(q.x - p.x, q.y - p.y),
      x: (p.x + q.x) / 2 - rect.left,
      y: (p.y + q.y) / 2 - rect.top,
    };
  }

  /**
   * The cursor the current mode implies.
   *
   * Driven from the mode rather than from pointer movement: setting it only on
   * move meant the drawing crosshair survived leaving merge mode until the user
   * happened to move the mouse again.
   */
  private modeCursor(): string {
    return this.input.merging ? 'crosshair' : '';
  }

  private syncCursor(): void {
    const wanted = this.modeCursor();
    if (this.canvas.style.cursor !== wanted) this.canvas.style.cursor = wanted;
  }

  /** Pointer position in board millimetres. */
  private toWorld(ev: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: this.camera.toWorldX(ev.clientX - rect.left),
      y: this.camera.toWorldY(ev.clientY - rect.top),
    };
  }

  /** Within grabbing distance of the divider? */
  private nearSplit(ev: PointerEvent): boolean {
    const rect = this.canvas.getBoundingClientRect();
    const view = this.viewport();
    return Math.abs(ev.clientX - rect.left - splitPixels(this.input.split, view.width)) <= SPLIT_GRAB_PX;
  }

  private moveSplit(ev: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const view = this.viewport();
    const fraction = clampSplit((ev.clientX - rect.left) / Math.max(view.width, 1));
    this.input = { ...this.input, split: fraction };
    this.onSplitChange?.(fraction);
    this.requestDraw();
  }

  private pick(ev: PointerEvent): string | undefined {
    const changes = this.input.diff?.substantive ?? [];
    const rect = this.canvas.getBoundingClientRect();
    const x = this.camera.toWorldX(ev.clientX - rect.left);
    const y = this.camera.toWorldY(ev.clientY - rect.top);
    const slack = this.camera.pixelSize(3);
    let best: ObjectChange | undefined;
    let bestArea = Infinity;
    for (const c of changes) {
      const b = c.bbox;
      if (b === null) continue;
      if (x < b.minX - slack || x > b.maxX + slack || y < b.minY - slack || y > b.maxY + slack) continue;
      const area = (b.maxX - b.minX) * (b.maxY - b.minY);
      if (area < bestArea) {
        bestArea = area;
        best = c;
      }
    }
    return best?.key;
  }

  // --- drawing ---------------------------------------------------------------------

  private draw(): void {
    const view = this.viewport();
    const dpr = globalThis.devicePixelRatio || 1;
    const ctx = this.ctx;

    if (this.canvas.width !== view.width * dpr || this.canvas.height !== view.height * dpr) {
      this.canvas.width = view.width * dpr;
      this.canvas.height = view.height * dpr;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, view.width, view.height);
    ctx.fillStyle = '#14141a';
    ctx.fillRect(0, 0, view.width, view.height);

    const { mode, blend, tintBoards, emphasiseChanges, diff } = this.input;
    const emphasise = emphasiseChanges && diff ? changedObjectIds(diff) : undefined;

    const base: RenderOptions = {
      visibleLayers: this.input.visibleLayers,
      opacity: 1,
      dimmedOpacity: 0.14,
      drawText: this.camera.scale > 3,
      ...(emphasise ? { emphasise } : {}),
    };

    let shapes = 0;
    let culled = 0;
    let texts = 0;
    let ms = 0;
    const accumulate = (s: { shapesDrawn: number; shapesCulled: number; textsDrawn: number; ms: number }): void => {
      shapes += s.shapesDrawn;
      culled += s.shapesCulled;
      texts += s.textsDrawn;
      ms += s.ms;
    };

    if (mode === 'a' && this.listA) {
      accumulate(this.renderer.draw(this.listA, this.camera, view, base));
    } else if (mode === 'b' && this.listB) {
      accumulate(this.renderer.draw(this.listB, this.camera, view, base));
    } else if (mode === 'split') {
      // Both boards through one camera, each clipped to its side of the
      // divider, so the geometry lines up across the seam and dragging it
      // sweeps one version over the other.
      const splitPx = splitPixels(this.input.split, view.width);
      const half = (list: DisplayList, x: number, width: number, tint: string): void => {
        if (width <= 0) return;
        ctx.save();
        // Clip in screen space. A clip region is fixed in device pixels once
        // set, so the camera transform the renderer installs does not disturb it.
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.beginPath();
        ctx.rect(x, 0, width, view.height);
        ctx.clip();
        accumulate(this.renderer.draw(list, this.camera, view, { ...base, ...(tintBoards ? { tint } : {}) }));
        ctx.restore();
      };
      if (this.listA) half(this.listA, 0, splitPx, TINT_A);
      if (this.listB) half(this.listB, splitPx, view.width - splitPx, TINT_B);
    } else if (mode === 'onion') {
      // Board A underneath, Board B on top; the slider fades between them.
      if (this.listA) {
        accumulate(
          this.renderer.draw(this.listA, this.camera, view, {
            ...base,
            opacity: Math.min(1, (1 - blend) * 2),
            ...(tintBoards ? { tint: TINT_A } : {}),
          }),
        );
      }
      if (this.listB) {
        accumulate(
          this.renderer.draw(this.listB, this.camera, view, {
            ...base,
            opacity: Math.min(1, blend * 2),
            ...(tintBoards ? { tint: TINT_B } : {}),
          }),
        );
      }
    }

    for (const selection of this.input.selections) {
      this.drawSelection(selection, selection.id === this.input.activeSelectionId, dpr);
    }
    if (this.input.drawing) this.drawRubberBand(this.input.drawing, dpr);
    if (this.input.showOverlay && diff) this.drawOverlay(diff, dpr);
    if (mode === 'split') this.drawSplitHandle(view);
    this.drawScaleBar(view);

    this.onStats?.({
      shapes,
      culled,
      texts,
      ms,
      placeholdersA: this.listA?.placeholders ?? 0,
      placeholdersB: this.listB?.placeholders ?? 0,
    });
  }

  private drawSelection(selection: Selection, active: boolean, dpr: number): void {
    const ctx = this.ctx;
    const box = rectBox(selection.rect);
    const colour = selectionColor(this.input.selections.indexOf(selection));

    ctx.save();
    this.camera.applyTo(ctx, dpr);
    // A faint wash so the area reads as claimed rather than merely outlined.
    ctx.fillStyle = colour;
    ctx.globalAlpha = active ? 0.12 : 0.07;
    ctx.fillRect(box.minX, box.minY, box.maxX - box.minX, box.maxY - box.minY);
    ctx.setLineDash([this.camera.pixelSize(8), this.camera.pixelSize(5)]);
    ctx.strokeStyle = colour;
    ctx.lineWidth = this.camera.pixelSize(active ? 2 : 1);
    ctx.globalAlpha = active ? 1 : 0.55;
    ctx.strokeRect(box.minX, box.minY, box.maxX - box.minX, box.maxY - box.minY);
    ctx.restore();

    ctx.save();
    ctx.fillStyle = colour;
    ctx.globalAlpha = active ? 1 : 0.6;
    ctx.font = '11px ui-monospace, monospace';
    ctx.fillText(selection.name, this.camera.toScreenX(box.minX) + 4, this.camera.toScreenY(box.minY) - 5);
    ctx.restore();
  }

  /** The rectangle currently being dragged out. */
  private drawRubberBand(rect: Rect, dpr: number): void {
    const ctx = this.ctx;
    ctx.save();
    this.camera.applyTo(ctx, dpr);
    ctx.fillStyle = SELECTED_COLOR;
    ctx.globalAlpha = 0.1;
    ctx.fillRect(rect.minX, rect.minY, rect.maxX - rect.minX, rect.maxY - rect.minY);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = SELECTED_COLOR;
    ctx.lineWidth = this.camera.pixelSize(1.5);
    ctx.setLineDash([this.camera.pixelSize(4), this.camera.pixelSize(4)]);
    ctx.strokeRect(rect.minX, rect.minY, rect.maxX - rect.minX, rect.maxY - rect.minY);
    ctx.restore();
  }

  private drawOverlay(diff: DiffResult, dpr: number): void {
    const ctx = this.ctx;
    const view = this.viewport();
    const visible = this.camera.visibleBox(view);
    const taken = this.input.takenKeys;

    ctx.save();
    this.camera.applyTo(ctx, dpr);
    ctx.lineJoin = 'miter';

    for (const change of diff.substantive) {
      const box = change.bbox;
      if (box === null || !intersects(box, visible)) continue;

      const selected = change.key === this.input.selectedKey;
      const pad = this.camera.pixelSize(2);
      ctx.strokeStyle = selected ? SELECTED_COLOR : changeColor(change.kind);
      ctx.lineWidth = this.camera.pixelSize(selected ? 2.5 : 1.25);
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // `change.bbox` is the union of where the object was and where it went.
      // For something that moved across the board that union is a rectangle
      // spanning the whole journey, which hides everything underneath it and
      // says nothing useful. Box the two positions instead and let the arrow
      // carry the relationship.
      const boxes = travelledFar(change, this.camera.pixelSize(1))
        ? [change.a!.bbox!, change.b!.bbox!]
        : [box];
      for (const b of boxes) {
        ctx.strokeRect(b.minX - pad, b.minY - pad, b.maxX - b.minX + pad * 2, b.maxY - b.minY + pad * 2);
      }

      // While merging, a change no selection claims is the thing most worth
      // noticing — what you forget is the dangerous half. Ring it so it cannot
      // be scrolled past.
      if (taken !== undefined) {
        if (!taken.has(change.key)) {
          const ring = this.camera.pixelSize(5);
          ctx.strokeStyle = CHANGE_COLOR.removed;
          ctx.lineWidth = this.camera.pixelSize(1);
          ctx.setLineDash([this.camera.pixelSize(3), this.camera.pixelSize(3)]);
          for (const b of boxes) {
            ctx.strokeRect(b.minX - ring, b.minY - ring, b.maxX - b.minX + ring * 2, b.maxY - b.minY + ring * 2);
          }
          ctx.setLineDash([]);
        }
      }

      if (change.displacement && change.a?.bbox && change.b?.bbox) {
        this.drawArrow(change.a.bbox, change.b.bbox, selected);
      }
    }
    ctx.restore();
  }

  private drawArrow(from: BBox, to: BBox, selected: boolean): void {
    const ctx = this.ctx;
    const x1 = (from.minX + from.maxX) / 2;
    const y1 = (from.minY + from.maxY) / 2;
    const x2 = (to.minX + to.maxX) / 2;
    const y2 = (to.minY + to.maxY) / 2;
    const len = Math.hypot(x2 - x1, y2 - y1);
    if (len * this.camera.scale < 6) return;

    ctx.save();
    ctx.strokeStyle = selected ? SELECTED_COLOR : CHANGE_COLOR.moved;
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineWidth = this.camera.pixelSize(selected ? 2 : 1.25);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    const angle = Math.atan2(y2 - y1, x2 - x1);
    const head = Math.min(this.camera.pixelSize(9), len / 3);
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - head * Math.cos(angle - 0.4), y2 - head * Math.sin(angle - 0.4));
    ctx.lineTo(x2 - head * Math.cos(angle + 0.4), y2 - head * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  /** The divider, its grip, and which board is on which side. */
  private drawSplitHandle(view: { width: number; height: number }): void {
    const ctx = this.ctx;
    const x = splitPixels(this.input.split, view.width);

    ctx.save();
    ctx.strokeStyle = '#e4e4ea';
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, view.height);
    ctx.stroke();

    // Grip, so it is obvious the line can be dragged.
    const gy = view.height / 2;
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#232329';
    ctx.strokeStyle = '#9a9aa8';
    // A plain rect rather than roundRect: the latter is a recent addition and
    // this needs no more than a grip.
    ctx.beginPath();
    ctx.rect(x - 7, gy - 22, 14, 44);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#9a9aa8';
    for (const dy of [-8, 0, 8]) {
      ctx.beginPath();
      ctx.arc(x, gy + dy, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.font = '11px ui-monospace, monospace';
    ctx.textBaseline = 'top';
    if (x > 30) {
      ctx.fillStyle = this.input.tintBoards ? TINT_A : '#9a9aa8';
      ctx.textAlign = 'right';
      ctx.fillText('A', x - 10, 8);
    }
    if (view.width - x > 30) {
      ctx.fillStyle = this.input.tintBoards ? TINT_B : '#9a9aa8';
      ctx.textAlign = 'left';
      ctx.fillText('B', x + 10, 8);
    }
    ctx.restore();
  }

  private drawScaleBar(view: { width: number; height: number }): void {
    const ctx = this.ctx;
    const targetMm = 100 / this.camera.scale;
    const pow = Math.pow(10, Math.floor(Math.log10(Math.max(targetMm, 1e-6))));
    const nice = [1, 2, 5, 10].map((m) => m * pow).find((v) => v >= targetMm) ?? pow * 10;
    const px = nice * this.camera.scale;
    const x = view.width - px - 16;
    const y = view.height - 16;

    ctx.save();
    ctx.strokeStyle = '#9a9aa8';
    ctx.fillStyle = '#9a9aa8';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y - 4);
    ctx.lineTo(x, y);
    ctx.lineTo(x + px, y);
    ctx.lineTo(x + px, y - 4);
    ctx.stroke();
    ctx.font = '10px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${nice} mm`, x + px / 2, y - 6);
    ctx.restore();
  }
}

function changeColor(kind: ObjectChange['kind']): string {
  switch (kind) {
    case 'added':
      return CHANGE_COLOR.added;
    case 'removed':
      return CHANGE_COLOR.removed;
    case 'modified':
    case 'moved':
      return CHANGE_COLOR.modified;
    case 'unchanged':
      return '#4a4a58';
  }
}

/**
 * Did this object move far enough that boxing the whole journey would obscure
 * the board?
 *
 * "Far" is relative to the object: a 0402 shifted 5 mm has travelled a long way,
 * a 40 mm connector nudged 5 mm has not. Requiring a visible on-screen distance
 * as well keeps the two boxes from being drawn on top of each other at low zoom.
 */
function travelledFar(change: ObjectChange, pixel: number): boolean {
  const a = change.a?.bbox;
  const b = change.b?.bbox;
  if (!a || !b || !change.displacement) return false;

  const distance = Math.hypot(change.displacement[0], change.displacement[1]);
  const size = Math.max(a.maxX - a.minX, a.maxY - a.minY, b.maxX - b.minX, b.maxY - b.minY, 0.01);
  return distance > size && distance > pixel * 12;
}

/** How close the pointer must be, in pixels, to grab the split divider. */
const SPLIT_GRAB_PX = 8;

/** Keep a sliver of both boards visible, so the divider can always be found. */
function clampSplit(fraction: number): number {
  if (!Number.isFinite(fraction)) return 0.5;
  return Math.min(0.99, Math.max(0.01, fraction));
}

function splitPixels(fraction: number, width: number): number {
  return clampSplit(fraction) * width;
}

/** Ids of every object touched by a substantive change, on either side. */
export function changedObjectIds(diff: DiffResult): Set<string> {
  const out = new Set<string>();
  for (const c of diff.substantive) {
    if (c.a) out.add(c.a.id);
    if (c.b) out.add(c.b.id);
  }
  return out;
}
